import clsx from 'clsx';
import React from 'react';
import Dialog, { type DialogProps as MuiDialogProps } from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from 'app/components/DialogTitle';
import AppBar, { type AppBarProps } from '@mui/material/AppBar';
import { alpha } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { type ButtonProps, Link, Tooltip } from '@mui/material';
import { getUserPreferences } from 'app/store/reducers';
import { useSelector } from 'react-redux';
import StandardButton from '../StandardButton';

const useStyles = makeStyles((theme) => ({
	dialog: {
		'& .MuiPaper-rounded': {
			borderRadius: 4,
		},
		'& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
			borderColor: '#f44336',
		},
	},
	errorDialogHeader: {
		color: '#FFFFFF !important',
		backgroundColor: '#A32525 !important',
	},
	dialogContent: {
		fontSize: '1.4rem',
		whiteSpace: 'pre-line',
		marginBottom: '8px',
	},
	dialogTitle: {
		whiteSpace: 'nowrap',
		overflowX: 'hidden',
		textOverflow: 'ellipsis',
	},
	button: {
		'&.MuiButton-root': {
			padding: '0 38px',
			borderRadius: 5,
		},
		'&.MuiButton-outlinedSecondary': {
			border: `2px solid ${alpha(theme.palette.secondary.main, 0.5)}`,
		},
		'&.MuiButton-outlinedSecondary:hover': {
			border: `2px solid ${theme.palette.secondary.main}`,
		},
		'&.MuiButton-containedSecondary': {
			border: `2px solid ${alpha(theme.palette.secondary.main, 0.5)}`,
		},
		'&.MuiButton-containedSecondary:hover': {
			border: '2px solid rgb(71, 44, 89)',
		},
	},
	resource: {
		overflow: 'visible',
		marginTop: 8,
		paddingTop: 0,
		paddingBottom: 0,
		paddingRight: 23,
		paddingLeft: 23,
	},
	input: {
		'& .MuiInputBase-root': {
			height: 36.5,
		},
	},
	warning: {
		'& .MuiDialog-paper': {
			border: '5px solid red',
		},
	},
}));

type Props = {
	open: boolean;
	setOpen: (open: boolean) => void;
	onOpen?: Function;
	onClose?: Function;
	title?: React.ReactNode;
	content?: React.ReactNode;
	action: (params?: any) => void;
	actionDoesNotClose?: boolean;
	buttonsText?: [any?, any?];
	type?: 'error' | 'warning' | 'warning-buttons';
	children?: React.ReactElement;
	contentTextVariant?: any;
	leftButton?: { text: string; action: () => void; color?: 'secondary' | 'error' };
	tertiaryButton?: { text: string; action: () => void };
	resource?: any;
	input?: any;
	disabledBtn?: boolean;
	overflowWithin?: boolean;
	maxWidth?: false | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | undefined;
	DialogProps?: Partial<MuiDialogProps>;
	backFn?: () => void;
	actionPaddingTopPx?: number;
	resourceMarginBottom?: number;
	secondaryButton?: boolean;
	noOverflow?: boolean;
	titleTooltip?: boolean;
	bonus?: any;
	noButton?: boolean;
	noButtonContainer?: boolean;
	className?: string;
	customButton?: React.ReactNode;
	titleIcon?: React.ReactNode;
	appBarProps?: AppBarProps;
	confirmation?: {
		helpText: string;
		secondaryAction?: () => void;
	};
	// `primaryButtonProps?: Partial<ButtonProps>;` should work but...it doesn't
	primaryButtonProps?: any;
};

const AlertDialog = ({
	open,
	setOpen,
	onOpen = () => {},
	onClose = () => {},
	children,
	title,
	content,
	action,
	actionDoesNotClose = false,
	buttonsText = [],
	type = undefined,
	contentTextVariant = 'caption',
	tertiaryButton,
	leftButton,
	actionPaddingTopPx,
	resourceMarginBottom,
	resource = null,
	input = null,
	disabledBtn = false,
	overflowWithin,
	maxWidth = 'xs',
	DialogProps,
	backFn,
	secondaryButton = true,
	noOverflow,
	titleTooltip = false,
	bonus,
	noButton = false,
	noButtonContainer = false,
	className = '',
	customButton,
	titleIcon,
	appBarProps = {},
	confirmation,
	primaryButtonProps,
}: Props) => {
	const { t } = useTranslation();
	const classes = useStyles();
	const userPreferences = useSelector(getUserPreferences);

	const [primaryButtonText = t('OK'), secondaryButtonText = t('Cancel')] = buttonsText;

	const handleOpen = () => {
		onOpen();
		setOpen(true);
	};

	const handleClose = () => {
		setOpen(false);
		onClose();
	};

	return (
		<>
			{children
				? React.cloneElement(children, {
						onClick: (e: any) => {
							e?.preventDefault?.();
							handleOpen();
						},
					})
				: undefined}
			{open && (
				<Dialog
					open={!!open}
					onClose={handleClose}
					className={`md ${classes.dialog} ${type === 'warning' && classes.warning} ${className}`}
					fullWidth
					maxWidth={maxWidth}
					aria-labelledby="dialog-title"
					disableRestoreFocus
					{...DialogProps}
				>
					<AppBar
						position="static"
						elevation={0}
						className={clsx({ [classes.errorDialogHeader]: type === 'error' }, appBarProps.className)}
						{...appBarProps}
					>
						{title && (
							<DialogTitle onClose={handleClose} className="flex w-full justify-center">
								<div className="flex flex-row">
									{titleIcon && (
										<div className="flex -ml-16 mr-8 items-center h-auto">{titleIcon}</div>
									)}
									<Tooltip placement="bottom" title={titleTooltip ? title : ''}>
										<Typography
											className={`text-base font-bold ${classes.dialogTitle}`}
											variant="subtitle1"
											color="inherit"
										>
											{title}
										</Typography>
									</Tooltip>
								</div>
							</DialogTitle>
						)}
					</AppBar>

					<main style={{ overflowY: noOverflow ? 'unset' : 'auto' }}>
						{(content || input) && (
							<DialogContent
								style={{
									padding: '23px 23px 0 23px',
								}}
								classes={{ root: 'p-16 pb-0 sm:p-24 sm:pb-0' }}
							>
								{content && (
									<DialogContentText
										className={classes.dialogContent}
										variant={contentTextVariant}
										color="inherit"
									>
										{content}
									</DialogContentText>
								)}
								{input && (
									<div
										style={{ paddingBottom: 23, paddingTop: 8, float: 'right', width: '100%' }}
										className={`form-group flex gap-20 ${classes.input}`}
									>
										{input}
										{/* <Button
								variant="contained"
								color="secondary"
								type="submit"
								onClick={action}
								disabled={disabledBtn}
								className="w-1/4 py-0 px-16"
								style={{ maxHeight: 31 }}
							>
								{primaryButtonText}
							</Button> */}
										<StandardButton
											btnText={primaryButtonText}
											onClick={action}
											disabled={disabledBtn}
											{...primaryButtonProps}
										/>
									</div>
								)}
							</DialogContent>
						)}

						{resource && (
							<DialogContent
								className={`${classes.resource} ${
									overflowWithin ? 'overflow-scroll mt-0' : ''
								} mb-${resourceMarginBottom ?? 8}`}
								// style={{ border: type === 'warning' ? '5px solid red' : 'none', borderBottom: 'none' }}
							>
								{resource}
							</DialogContent>
						)}
					</main>

					{!input && !noButtonContainer && (
						<DialogActions
							style={{
								padding: `${actionPaddingTopPx ?? '8'}px 23px 23px 23px`,
								// border: type === 'warning' ? '5px solid red' : 'none',
								borderTop: 'none',
							}}
							className={`px-24 pb-20 ${confirmation ? 'justify-center flex' : ''}`}
							disableSpacing
						>
							<div style={{ position: 'absolute', left: 23 }}>{bonus}</div>
							{backFn && (
								<StandardButton
									className="mr-12"
									btnText={t('Back')}
									onClick={backFn}
									btnType="secondary"
									style={{ position: 'absolute', left: 23 }}
								/>
							)}
							{leftButton && (
								<StandardButton
									className="mr-12"
									btnText={leftButton.text}
									onClick={leftButton.action}
									color={leftButton.color === 'error' ? undefined : (leftButton.color ?? 'secondary')}
									btnType={!leftButton.color ? 'secondary' : undefined}
									style={{
										position: 'absolute',
										left: 23,
										...(leftButton.color === 'error' && {
											color: '#fff',
											backgroundColor: '#CC0203',
										}),
									}}
								/>
							)}
							{tertiaryButton && (
								<StandardButton
									className="mr-12"
									btnText={tertiaryButton.text}
									onClick={tertiaryButton.action}
									btnType="secondary"
									style={{
										...(type === 'warning-buttons' && { borderColor: 'red', color: 'red' }),
									}}
								/>
							)}
							{confirmation ? (
								<div
									style={{
										display: 'flex',
										width: '100%',
										position: 'relative',
										justifyContent: 'center',
									}}
								>
									<div
										style={{
											position: 'absolute',
											left: 0,
											maxWidth: '35%',
											fontSize: '1.1rem',
											textAlign: 'left',
											lineHeight: 1.3,
											paddingRight: 16,
											display: 'flex',
											alignItems: 'center',
											height: '100%',
										}}
									>
										{confirmation.helpText}
									</div>
									<div
										style={{
											display: 'flex',
											justifyContent: 'center',
											alignItems: 'center',
										}}
									>
										<StandardButton
											className="mr-12"
											btnText={secondaryButtonText}
											onClick={confirmation.secondaryAction || handleClose}
											btnType="secondary"
											style={{
												border: '1px solid #cc0203',
												color: '#cc0203',
												background: '#fff',
											}}
										/>
										<StandardButton
											btnText={primaryButtonText}
											disabled={disabledBtn}
											onClick={(e: any) => {
												action(e);
												if (!actionDoesNotClose) {
													setOpen(false);
												}
											}}
											style={{
												border: '1px solid #cc0203',
												color: '#fff',
												background: '#cc0203',
											}}
											{...primaryButtonProps}
										/>
									</div>
								</div>
							) : (
								<>
									{((resource && buttonsText[1]) || !resource) &&
										secondaryButton &&
										secondaryButtonText && (
											<StandardButton
												className="mr-12"
												btnText={secondaryButtonText}
												onClick={handleClose}
												btnType="secondary"
												style={
													type === 'warning' || type === 'warning-buttons'
														? { borderColor: 'red', color: 'red' }
														: userPreferences?.appearance === 'dark'
															? { borderColor: '#8E5EF4', color: '#8E5EF4' }
															: {}
												}
											/>
										)}
									{!noButton && (
										<StandardButton
											btnText={primaryButtonText}
											disabled={disabledBtn}
											onClick={(e: any) => {
												const result = action(e);
												if (!actionDoesNotClose) {
													setOpen(false);
												}
											}}
											style={
												(type === 'warning' || type === 'warning-buttons') && !disabledBtn
													? {
															background: 'red',
															color: 'white',
														}
													: undefined
											}
											{...primaryButtonProps}
										/>
									)}
								</>
							)}
							{customButton}
						</DialogActions>
					)}
				</Dialog>
			)}
		</>
	);
};

export default AlertDialog;
